import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

export function useCrossSection(refs, t) {
    const { sceneRef, objectsRef, pivotRef, maxillaMeshesRef, mandibleMeshesRef } = refs;

    const [crossSectionEnabled, setCrossSectionEnabled] = useState(false);
    const [crossSectionAxis, setCrossSectionAxis] = useState('y');
    const [crossSectionPosition, setCrossSectionPosition] = useState(0);
    const [crossSectionAngle, setCrossSectionAngle] = useState(0);
    const [crossSectionPlaneOpacity, setCrossSectionPlaneOpacity] = useState(0.3);
    const [crossSectionDialogOpen, setCrossSectionDialogOpen] = useState(false);
    const [crossSectionFullscreen, setCrossSectionFullscreen] = useState(false);
    const [csMeasureMode, setCsMeasureMode] = useState(false);
    const [csMeasurements, setCsMeasurements] = useState([]);
    const [csMeasurePending, setCsMeasurePending] = useState(null);

    const clippingPlaneRef = useRef(new THREE.Plane(new THREE.Vector3(0, -1, 0), 0));
    const crossSectionHelperRef = useRef(null);
    const crossSectionCanvasRef = useRef(null);
    const crossSectionRendererRef = useRef(null);
    const crossSectionAnimRef = useRef(0);
    const csOutlineSceneRef = useRef(new THREE.Scene());
    const csOutlineCameraRef = useRef(new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 100));
    const csOutlineCanvasRef = useRef(null);
    const csOutlineLinesRef = useRef(null);
    const prevMeasureCountRef = useRef(0);

    useEffect(() => {
        const plane = clippingPlaneRef.current;
        const rad = (crossSectionAngle * Math.PI) / 180;

        if (crossSectionAxis === 'x') {
            plane.normal.set(-Math.cos(rad), 0, -Math.sin(rad));
        } else if (crossSectionAxis === 'y') {
            plane.normal.set(Math.sin(rad), -Math.cos(rad), 0);
        } else {
            plane.normal.set(0, Math.sin(rad), -Math.cos(rad));
        }

        if (objectsRef.current.length > 0) {
            const box = new THREE.Box3();
            for (const obj of objectsRef.current) box.expandByObject(obj);
            const center = new THREE.Vector3();
            box.getCenter(center);
            const size = new THREE.Vector3();
            box.getSize(size);

            if (crossSectionAxis === 'x') {
                const halfExtent = Math.max(size.x, size.z) / 2;
                plane.constant = plane.normal.dot(center) + crossSectionPosition * halfExtent;
            } else if (crossSectionAxis === 'y') {
                const halfExtent = Math.max(size.x, size.y) / 2;
                plane.constant = plane.normal.dot(center) + crossSectionPosition * halfExtent;
            } else {
                const halfExtent = Math.max(size.y, size.z) / 2;
                plane.constant = plane.normal.dot(center) + crossSectionPosition * halfExtent;
            }
        }

        const allMeshes = [...maxillaMeshesRef.current, ...mandibleMeshesRef.current];
        for (const mesh of allMeshes) {
            const mat = mesh.material;
            mat.clippingPlanes = crossSectionEnabled ? [plane] : [];
            mat.needsUpdate = true;
        }
    }, [crossSectionEnabled, crossSectionAxis, crossSectionPosition, crossSectionAngle, objectsRef, maxillaMeshesRef, mandibleMeshesRef]);

    useEffect(() => {
        const scene = sceneRef.current;
        if (!scene) return;

        if (crossSectionHelperRef.current) {
            scene.remove(crossSectionHelperRef.current);
            crossSectionHelperRef.current.geometry.dispose();
            crossSectionHelperRef.current.material.dispose();
            crossSectionHelperRef.current = null;
        }

        if (!crossSectionEnabled || objectsRef.current.length === 0) return;

        const box = new THREE.Box3();
        for (const obj of objectsRef.current) box.expandByObject(obj);
        const size = new THREE.Vector3();
        box.getSize(size);

        const planeSize = Math.max(size.x, size.y, size.z) * 1.3;
        const geo = new THREE.PlaneGeometry(planeSize, planeSize);
        const mat = new THREE.MeshBasicMaterial({
            color: 0x44aaff,
            transparent: true,
            opacity: crossSectionPlaneOpacity,
            side: THREE.DoubleSide,
            depthWrite: false,
        });
        const helper = new THREE.Mesh(geo, mat);

        const plane = clippingPlaneRef.current;
        const coplanarPt = new THREE.Vector3();
        plane.coplanarPoint(coplanarPt);
        helper.position.copy(coplanarPt);

        const target = coplanarPt.clone().add(plane.normal);
        helper.lookAt(target);

        helper.renderOrder = 999;
        scene.add(helper);
        crossSectionHelperRef.current = helper;
    }, [crossSectionEnabled, crossSectionAxis, crossSectionPosition, crossSectionAngle, crossSectionPlaneOpacity, sceneRef, objectsRef]);

    useEffect(() => {
        if (crossSectionHelperRef.current) {
            crossSectionHelperRef.current.material.opacity = crossSectionPlaneOpacity;
        }
    }, [crossSectionPlaneOpacity]);

    useEffect(() => {
        const outlineScene = csOutlineSceneRef.current;
        if (csOutlineLinesRef.current) {
            outlineScene.remove(csOutlineLinesRef.current);
            csOutlineLinesRef.current.geometry.dispose();
            csOutlineLinesRef.current.material.dispose();
            csOutlineLinesRef.current = null;
        }

        if (!crossSectionEnabled || objectsRef.current.length === 0) return;

        const plane = clippingPlaneRef.current.clone();
        const segments = [];

        pivotRef.current.updateMatrixWorld(true);
        const allMeshes = [];
        for (const obj of objectsRef.current) {
            obj.traverse((child) => {
                if (child instanceof THREE.Mesh) allMeshes.push(child);
            });
        }

        for (const mesh of allMeshes) {
            const geom = mesh.geometry;
            const posAttr = geom.attributes.position;
            const index = geom.index;
            const triCount = index ? index.count / 3 : posAttr.count / 3;

            for (let ti = 0; ti < triCount; ti++) {
                const i0 = index ? index.getX(ti * 3) : ti * 3;
                const i1 = index ? index.getX(ti * 3 + 1) : ti * 3 + 1;
                const i2 = index ? index.getX(ti * 3 + 2) : ti * 3 + 2;

                const v0 = new THREE.Vector3().fromBufferAttribute(posAttr, i0).applyMatrix4(mesh.matrixWorld);
                const v1 = new THREE.Vector3().fromBufferAttribute(posAttr, i1).applyMatrix4(mesh.matrixWorld);
                const v2 = new THREE.Vector3().fromBufferAttribute(posAttr, i2).applyMatrix4(mesh.matrixWorld);

                const d0 = plane.distanceToPoint(v0);
                const d1 = plane.distanceToPoint(v1);
                const d2 = plane.distanceToPoint(v2);

                const pts = [];
                if (d0 * d1 < 0) {
                    const t01 = d0 / (d0 - d1);
                    pts.push(new THREE.Vector3().lerpVectors(v0, v1, t01));
                }
                if (d1 * d2 < 0) {
                    const t12 = d1 / (d1 - d2);
                    pts.push(new THREE.Vector3().lerpVectors(v1, v2, t12));
                }
                if (d2 * d0 < 0) {
                    const t20 = d2 / (d2 - d0);
                    pts.push(new THREE.Vector3().lerpVectors(v2, v0, t20));
                }
                if (d0 === 0) pts.push(v0.clone());
                if (d1 === 0) pts.push(v1.clone());
                if (d2 === 0) pts.push(v2.clone());

                if (pts.length >= 2) {
                    segments.push(pts[0].x, pts[0].y, pts[0].z, pts[1].x, pts[1].y, pts[1].z);
                }
            }
        }

        if (segments.length === 0) return;

        const planeNormal = plane.normal.clone().normalize();
        const upHint = Math.abs(planeNormal.dot(new THREE.Vector3(0, 1, 0))) > 0.9
            ? new THREE.Vector3(0, 0, 1)
            : new THREE.Vector3(0, 1, 0);
        const tangent = new THREE.Vector3().crossVectors(upHint, planeNormal).normalize();
        const bitangent = new THREE.Vector3().crossVectors(planeNormal, tangent).normalize();
        const getUV = (p) => [p.dot(tangent), p.dot(bitangent)];

        const positions2D = [];
        for (let i = 0; i < segments.length; i += 6) {
            const [u0, vv0] = getUV(new THREE.Vector3(segments[i], segments[i + 1], segments[i + 2]));
            const [u1, vv1] = getUV(new THREE.Vector3(segments[i + 3], segments[i + 4], segments[i + 5]));
            positions2D.push(u0, vv0, 0, u1, vv1, 0);
        }

        const lineGeo = new THREE.BufferGeometry();
        lineGeo.setAttribute('position', new THREE.Float32BufferAttribute(positions2D, 3));
        const lineMat = new THREE.LineBasicMaterial({ color: 0x7ecbf5, linewidth: 1 });
        const lineSegs = new THREE.LineSegments(lineGeo, lineMat);
        outlineScene.add(lineSegs);
        csOutlineLinesRef.current = lineSegs;

        lineGeo.computeBoundingBox();
        const bb = lineGeo.boundingBox;
        const ctr2 = new THREE.Vector3();
        bb.getCenter(ctr2);
        const sz2 = new THREE.Vector3();
        bb.getSize(sz2);
        const maxDim = Math.max(sz2.x, sz2.y) || 1;
        const padding = maxDim * 0.6;

        const cam = csOutlineCameraRef.current;
        cam.left = -padding;
        cam.right = padding;
        cam.top = padding;
        cam.bottom = -padding;
        cam.position.set(ctr2.x, ctr2.y, 10);
        cam.lookAt(ctr2.x, ctr2.y, 0);
        cam.updateProjectionMatrix();
    }, [crossSectionEnabled, crossSectionAxis, crossSectionPosition, crossSectionAngle, objectsRef, pivotRef]);

    const csMeasureOverlays = useMemo(() => {
        const cam = csOutlineCameraRef.current;
        const cw = cam.right - cam.left;
        const ch = cam.top - cam.bottom;
        if (!cw || !ch) return { lines: [], pending: null };
        const toPercent = (pt) => ({
            xPct: ((pt.x - cam.left) / cw) * 100,
            yPct: (1 - (pt.y - cam.bottom) / ch) * 100,
        });
        const lines = csMeasurements.map(([p1, p2]) => {
            const a = toPercent(p1);
            const b = toPercent(p2);
            const dx = p2.x - p1.x;
            const dy = p2.y - p1.y;
            return { x1Pct: a.xPct, y1Pct: a.yPct, x2Pct: b.xPct, y2Pct: b.yPct, dist: Math.sqrt(dx * dx + dy * dy) };
        });
        const pending = csMeasurePending ? toPercent(csMeasurePending) : null;
        return { lines, pending };
    }, [csMeasurements, csMeasurePending]);

    useEffect(() => {
        setCsMeasurements([]);
        setCsMeasurePending(null);
    }, [crossSectionAxis, crossSectionPosition, crossSectionAngle]);

    const captureCSMeasurement = useCallback((_setGallery) => {
        if (csMeasurements.length <= prevMeasureCountRef.current || csMeasurements.length === 0) {
            prevMeasureCountRef.current = csMeasurements.length;
            return null;
        }

        const canvas = csOutlineCanvasRef.current;
        if (!canvas) return null;
        const cam = csOutlineCameraRef.current;
        const cw = cam.right - cam.left;
        const ch = cam.top - cam.bottom;
        if (!cw || !ch) return null;

        const w = canvas.width || canvas.clientWidth;
        const h = canvas.height || canvas.clientHeight;
        const tmpCanvas = document.createElement('canvas');
        tmpCanvas.width = w;
        tmpCanvas.height = h;
        const ctx = tmpCanvas.getContext('2d');

        ctx.drawImage(canvas, 0, 0, w, h);

        for (const [p1, p2] of csMeasurements) {
            const x1 = ((p1.x - cam.left) / cw) * w;
            const y1 = (1 - (p1.y - cam.bottom) / ch) * h;
            const x2 = ((p2.x - cam.left) / cw) * w;
            const y2 = (1 - (p2.y - cam.bottom) / ch) * h;

            ctx.save();
            ctx.strokeStyle = '#d32f2f';
            ctx.lineWidth = 2;
            ctx.setLineDash([6, 4]);
            ctx.beginPath();
            ctx.moveTo(x1, y1);
            ctx.lineTo(x2, y2);
            ctx.stroke();
            ctx.restore();

            for (const [dx, dy] of [[x1, y1], [x2, y2]]) {
                ctx.beginPath();
                ctx.arc(dx, dy, 5, 0, Math.PI * 2);
                ctx.fillStyle = '#d32f2f';
                ctx.fill();
                ctx.strokeStyle = '#fff';
                ctx.lineWidth = 1;
                ctx.stroke();
            }

            const dist = Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2);
            const mx = (x1 + x2) / 2;
            const my = (y1 + y2) / 2;
            const label = `${dist.toFixed(2)} mm`;
            ctx.font = 'bold 14px sans-serif';
            const metrics = ctx.measureText(label);
            const pad = 4;
            ctx.fillStyle = 'rgba(0,0,0,0.8)';
            ctx.fillRect(mx - metrics.width / 2 - pad, my - 22, metrics.width + pad * 2, 20);
            ctx.fillStyle = '#fff';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(label, mx, my - 12);
        }

        const url = tmpCanvas.toDataURL('image/png');
        prevMeasureCountRef.current = csMeasurements.length;

        const axisLabel = crossSectionAxis === 'y' ? (t ? t('viewer3d.horiz') : 'Horiz') : crossSectionAxis === 'x' ? (t ? t('viewer3d.sagittal') : 'Sagittal') : (t ? t('viewer3d.coronal') : 'Coronal');
        return { url, label: `Cross Section ${axisLabel} #${csMeasurements.length}`, category: 'input' };
    }, [csMeasurements, crossSectionAxis, t]);

    const csViewActive = crossSectionDialogOpen || crossSectionFullscreen;
    useEffect(() => {
        if (!csViewActive) {
            if (crossSectionAnimRef.current) cancelAnimationFrame(crossSectionAnimRef.current);
            if (crossSectionRendererRef.current) {
                crossSectionRendererRef.current.dispose();
                crossSectionRendererRef.current = null;
            }
            return;
        }

        const timer = setTimeout(() => {
            const canvas = crossSectionCanvasRef.current;
            const scene = sceneRef.current;
            if (!canvas || !scene) return;

            const r = new THREE.WebGLRenderer({ canvas, antialias: true, preserveDrawingBuffer: true });
            r.localClippingEnabled = true;
            r.toneMapping = THREE.NoToneMapping;
            r.setClearColor(0x1e1e1e);
            r.setPixelRatio(Math.min(window.devicePixelRatio, 2));
            crossSectionRendererRef.current = r;

            const box = new THREE.Box3();
            for (const obj of objectsRef.current) box.expandByObject(obj);
            const ctr = new THREE.Vector3();
            box.getCenter(ctr);
            const sz = new THREE.Vector3();
            box.getSize(sz);
            const md = Math.max(sz.x, sz.y, sz.z) || 100;
            const pad = md * 0.8;

            const w = canvas.clientWidth;
            const h = canvas.clientHeight;
            const a = w / h || 1;
            const cam = new THREE.OrthographicCamera(-pad * a, pad * a, pad, -pad, 0.1, 50000);
            cam.position.set(ctr.x, ctr.y, ctr.z + md * 3);
            cam.lookAt(ctr);
            cam.updateProjectionMatrix();

            const dialogControls = new OrbitControls(cam, canvas);
            dialogControls.enableDamping = true;
            dialogControls.target.copy(ctr);

            const animateDialog = () => {
                crossSectionAnimRef.current = requestAnimationFrame(animateDialog);
                dialogControls.update();
                const cw = canvas.clientWidth;
                const ch = canvas.clientHeight;
                if (cw > 0 && ch > 0) {
                    r.setSize(cw, ch, false);
                    const da = cw / ch;
                    cam.left = -pad * da;
                    cam.right = pad * da;
                    cam.top = pad;
                    cam.bottom = -pad;
                    cam.updateProjectionMatrix();
                }
                r.render(scene, cam);
            };
            animateDialog();
        }, 100);

        return () => {
            clearTimeout(timer);
            if (crossSectionAnimRef.current) cancelAnimationFrame(crossSectionAnimRef.current);
            if (crossSectionRendererRef.current) {
                crossSectionRendererRef.current.dispose();
                crossSectionRendererRef.current = null;
            }
        };
    }, [csViewActive, sceneRef, objectsRef]);

    return {
        crossSectionEnabled, setCrossSectionEnabled,
        crossSectionAxis, setCrossSectionAxis,
        crossSectionPosition, setCrossSectionPosition,
        crossSectionAngle, setCrossSectionAngle,
        crossSectionPlaneOpacity, setCrossSectionPlaneOpacity,
        crossSectionDialogOpen, setCrossSectionDialogOpen,
        crossSectionFullscreen, setCrossSectionFullscreen,
        csMeasureMode, setCsMeasureMode,
        csMeasurements, setCsMeasurements,
        csMeasurePending, setCsMeasurePending,
        csMeasureOverlays,
        captureCSMeasurement,
        clippingPlaneRef,
        crossSectionCanvasRef,
        csOutlineSceneRef,
        csOutlineCameraRef,
        csOutlineCanvasRef,
        csOutlineLinesRef,
    };
}
